// src/components/CreatorSubmissionForm.js
import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { signOut } from 'firebase/auth';
import { db, auth, storage } from '../firebase';
import { COUNTRY_CODES } from '../utils/countryCodes';
import './CreatorSubmissionForm.css';

export default function CreatorSubmissionForm() {
  const { routeId } = useParams();
  const navigate = useNavigate();

  // Submission tracking
  const [submissionId, setSubmissionId] = useState(routeId || null);
  const [loading, setLoading] = useState(!!routeId);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [creatorStatus, setCreatorStatus] = useState('draft');
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true); // Auto-save toggle (default: ON)
  const [showAllFields, setShowAllFields] = useState(false); // Toggle to reveal all fields (default: OFF)

  const [routeName, setRouteName] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [cities, setCities] = useState([]);
  const [expandedCityId, setExpandedCityId] = useState(null);
  const [storeBulletPoints, setStoreBulletPoints] = useState([]);

  // Additional route information
  const [minDuration, setMinDuration] = useState('');
  const [maxDuration, setMaxDuration] = useState('');
  const [bestTimeToVisit, setBestTimeToVisit] = useState('');
  const [budgetEstimate, setBudgetEstimate] = useState('');
  const [difficultyLevel, setDifficultyLevel] = useState('');
  const [colorScheme, setColorScheme] = useState('');
  const [storyBehindRoute, setStoryBehindRoute] = useState('');
  const [transportationBetweenCities, setTransportationBetweenCities] = useState('');
  const [specialConsiderations, setSpecialConsiderations] = useState('');
  const [clothingRecommendations, setClothingRecommendations] = useState('');

  // Additional hostel suggestions
  const [suggestedHostels, setSuggestedHostels] = useState([]);

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const handleSaveDraftRef = useRef(null);

  // Filter countries based on search input
  const filteredCountries = COUNTRY_CODES.filter(country =>
    country.name.toLowerCase().includes(countrySearch.toLowerCase()) &&
    !selectedCountries.find(c => c.code === country.code)
  );

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load existing route data if editing
  useEffect(() => {
    const loadRouteData = async () => {
      if (!routeId) {
        // If creating a new route, check if user has reached their route limit
        try {
          const user = auth.currentUser;
          if (!user) {
            setLoading(false);
            return;
          }

          // Get user's maxRoutes limit
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const maxRoutes = userDoc.exists() ? (userDoc.data().maxRoutes || 1) : 1;

          // Count existing routes
          const routesQuery = query(
            collection(db, 'routes'),
            where('creatorId', '==', user.uid)
          );
          const routesSnapshot = await getDocs(routesQuery);
          const routeCount = routesSnapshot.size;

          if (routeCount >= maxRoutes) {
            alert(`You've reached your route limit (${maxRoutes} route${maxRoutes > 1 ? 's' : ''}). Please contact support if you'd like to create more routes.`);
            navigate('/creator-dashboard');
            return;
          }
        } catch (error) {
          console.error('Error checking route limit:', error);
        }
        setLoading(false);
        return;
      }

      try {
        const routeDoc = await getDoc(doc(db, 'routes', routeId));

        if (routeDoc.exists()) {
          const data = routeDoc.data();

          // Verify this route belongs to the current user
          if (data.creatorId !== auth.currentUser?.uid) {
            alert('You do not have permission to edit this route');
            navigate('/creator-dashboard');
            return;
          }

          // ====== MIGRATION HELPER FOR ADMIN-CREATED ROUTES ======
          // Check if this is an old admin-created route (has cityIds instead of cities array)
          const isAdminRoute = data.cityIds && Array.isArray(data.cityIds) && data.cityIds.length > 0 && (!data.cities || data.cities.length === 0);

          let loadedCities = [];
          let loadedCountries = [];

          if (isAdminRoute) {
            console.log('Detected admin-created route, migrating city data from global collection...');

            // Fetch city documents from global cities collection
            try {
              const cityDocs = await Promise.all(
                data.cityIds.map(cityId => getDoc(doc(db, 'cities', cityId)))
              );

              // Convert city documents to creator format
              loadedCities = cityDocs
                .filter(cityDoc => cityDoc.exists())
                .map(cityDoc => {
                  const cityData = cityDoc.data();
                  return {
                    id: cityDoc.id, // Keep original city ID from global collection
                    name: cityData.name || '',
                    countryCode: cityData.countryCode || '',
                    country: cityData.country || COUNTRY_CODES.find(c => c.code === cityData.countryCode)?.name || '',
                    coverImage: cityData.coverImage || null,
                    coverImagePreview: cityData.coverImage || null, // Use URL as preview
                    images: cityData.images || [],
                    imagesPreviews: cityData.images || [], // Use URLs as previews
                    cityVibes: cityData.cityVibes || '',
                    tagline: cityData.tagline || '',
                    description: cityData.description || '',
                    latitude: cityData.latitude || '',
                    longitude: cityData.longitude || '',
                    attractions: cityData.attractions || '',
                    food: cityData.food || '',
                    transportation: cityData.transportation || '',
                    services: cityData.services || '',
                    testimonials: cityData.testimonials || '',
                    litHostel: '', // Admin routes don't have hostel recommendations
                    chillHostel: ''
                  };
                });

              // Infer selectedCountries from converted cities
              const uniqueCountryCodes = [...new Set(loadedCities.map(c => c.countryCode))];
              loadedCountries = uniqueCountryCodes
                .map(code => {
                  const country = COUNTRY_CODES.find(c => c.code === code);
                  return country ? { code: country.code, name: country.name } : null;
                })
                .filter(Boolean);

              console.log(`Migrated ${loadedCities.length} cities from global collection`);
            } catch (error) {
              console.error('Error fetching city data for admin route:', error);
              alert('Failed to load city data. Some cities may not display correctly.');
            }
          } else {
            // Normal creator-submitted route with embedded cities
            loadedCities = (data.cities || []).map(city => ({
              ...city,
              // Ensure cityVibes exists (backward compatibility)
              cityVibes: city.cityVibes || city.cityVibe || '',
              // Ensure country name is set from countryCode if missing
              country: city.country || COUNTRY_CODES.find(c => c.code === city.countryCode)?.name || '',
              // Ensure images arrays exist
              images: city.images || [],
              // If coverImage is a URL but no preview, use URL as preview (for existing routes)
              coverImagePreview: city.coverImagePreview || (typeof city.coverImage === 'string' ? city.coverImage : null),
              // If images are URLs but no previews, use URLs as previews (for existing routes)
              imagesPreviews: city.imagesPreviews && city.imagesPreviews.length > 0
                ? city.imagesPreviews
                : (city.images && city.images.length > 0 && typeof city.images[0] === 'string'
                    ? city.images
                    : []),
              // Ensure coordinates exist
              latitude: city.latitude || '',
              longitude: city.longitude || ''
            }));

            loadedCountries = data.selectedCountries || [];
          }

          // Set state with loaded/migrated data
          setCities(loadedCities);
          setSelectedCountries(loadedCountries);

          // Load route name - check both name (standard) and routeName (legacy) for backward compatibility
          setRouteName(data.name || data.routeName || '');

          // Load all other route data (same for both admin and creator routes)
          // Load storeBulletPoints and add IDs if they don't exist
          let bulletPoints = data.storeBulletPoints || [];
          bulletPoints = bulletPoints.map((point, index) => ({
            id: point.id || `highlight-${Date.now()}-${index}`,
            mainText: point.mainText || '',
            subText: point.subText || ''
          }));
          setStoreBulletPoints(bulletPoints);
          setMinDuration(data.minDuration || '');
          setMaxDuration(data.maxDuration || '');
          setBestTimeToVisit(data.bestTimeToVisit || '');
          setBudgetEstimate(data.budgetEstimate || '');
          setDifficultyLevel(data.difficultyLevel || '');
          setColorScheme(data.colorScheme || '');
          setStoryBehindRoute(data.storyBehindRoute || '');
          setTransportationBetweenCities(data.transportationBetweenCities || '');
          setSpecialConsiderations(data.specialConsiderations || '');
          setClothingRecommendations(data.clothingRecommendations || '');
          setSuggestedHostels(data.suggestedHostels || []);
          setCreatorStatus(data.creatorStatus || 'draft');
          setLastSaved(data.updatedAt);
        } else {
          alert('Route not found');
          navigate('/creator-dashboard');
        }
      } catch (error) {
        console.error('Error loading route:', error);
        alert('Failed to load route data');
      } finally {
        setLoading(false);
      }
    };

    loadRouteData();
  }, [routeId, navigate]);

  // Auto-save functionality (every 60 seconds)
  // SAFETY: Auto-save NEVER overwrites live or off_store routes (handled in handleSaveDraft)
  useEffect(() => {
    if (!submissionId || !autoSaveEnabled) return; // Don't auto-save if no submission ID or toggle is OFF

    const autoSaveInterval = setInterval(() => {
      handleSaveDraftRef.current?.(true); // Pass true to indicate it's an auto-save
    }, 60000); // 60 seconds

    return () => clearInterval(autoSaveInterval);
  }, [submissionId, autoSaveEnabled]); // Only reset interval when these change

  const handleSelectCountry = (country) => {
    setSelectedCountries([...selectedCountries, country]);
    setCountrySearch('');
    setShowCountryDropdown(false);
  };

  const handleRemoveCountry = (countryCode) => {
    setSelectedCountries(selectedCountries.filter(c => c.code !== countryCode));
    // Also remove cities from that country
    setCities(cities.filter(city => city.countryCode !== countryCode));
  };

  // City management functions
  const handleAddCity = () => {
    if (cities.length >= 6) {
      alert('Maximum of 6 cities allowed per route');
      return;
    }

    const selectedCountry = selectedCountries[0];
    const newCity = {
      id: `city-${Date.now()}`,
      name: '',
      countryCode: selectedCountry?.code || '',
      country: selectedCountry?.name || '',
      coverImage: null,
      coverImagePreview: null,
      images: [], // Additional city images array
      imagesPreviews: [], // Preview URLs for additional images
      cityVibes: '', // MATCH existing global cities field name (plural)
      tagline: '',
      description: '',
      latitude: '',
      longitude: '',
      attractions: '',
      food: '',
      transportation: '',
      services: '',
      testimonials: '',
      litHostel: '',
      chillHostel: ''
    };
    setCities([...cities, newCity]);
    setExpandedCityId(newCity.id);
  };

  const handleUpdateCity = (cityId, field, value) => {
    setCities(cities.map(city => {
      if (city.id === cityId) {
        const updates = { [field]: value };

        // If countryCode is being updated, also update country name
        if (field === 'countryCode') {
          const country = COUNTRY_CODES.find(c => c.code === value);
          updates.country = country ? country.name : '';
        }

        return { ...city, ...updates };
      }
      return city;
    }));
  };

  const handleRemoveCity = (cityId) => {
    if (window.confirm('Remove this city?')) {
      setCities(cities.filter(city => city.id !== cityId));
      if (expandedCityId === cityId) {
        setExpandedCityId(null);
      }
    }
  };

  const handleCityImageUpload = (cityId, file) => {
    if (file) {
      const reader = new FileReader();

      reader.onloadend = () => {
        setCities(cities.map(city =>
          city.id === cityId
            ? { ...city, coverImage: file, coverImagePreview: reader.result }
            : city
        ));
      };

      reader.onerror = (error) => {
        console.error(`Failed to read cover image "${file.name}":`, error);
        alert('Failed to load cover image. Please check that the file is a valid image format (JPG, PNG, etc.).');
      };

      reader.readAsDataURL(file);
    }
  };

  const handleCityAdditionalImagesUpload = (cityId, files) => {
    if (!files || files.length === 0) return;

    const city = cities.find(c => c.id === cityId);
    const currentImages = city?.images || [];
    const currentPreviews = city?.imagesPreviews || [];

    if (currentImages.length + files.length > 5) {
      alert('Maximum of 5 additional images allowed per city');
      return;
    }

    const fileArray = Array.from(files);
    const newImages = [...currentImages];
    const newPreviews = [...currentPreviews];

    let processed = 0;
    let successCount = 0;

    fileArray.forEach((file, index) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        // Successfully read the file - add it to arrays
        newImages.push(file);
        newPreviews.push(reader.result);
        successCount++;
        processed++;

        if (processed === fileArray.length) {
          setCities(prevCities => prevCities.map(city =>
            city.id === cityId
              ? { ...city, images: newImages, imagesPreviews: newPreviews }
              : city
          ));

          // Notify user if some files failed
          if (successCount < fileArray.length) {
            alert(`Some images could not be loaded. Successfully added ${successCount} of ${fileArray.length} images.`);
          }
        }
      };

      reader.onerror = (error) => {
        // Handle FileReader error
        console.error(`Failed to read file "${file.name}":`, error);
        processed++;

        // Continue processing remaining files
        if (processed === fileArray.length) {
          setCities(prevCities => prevCities.map(city =>
            city.id === cityId
              ? { ...city, images: newImages, imagesPreviews: newPreviews }
              : city
          ));

          // Notify user of failures
          if (successCount < fileArray.length) {
            alert(`Some images could not be loaded. Successfully added ${successCount} of ${fileArray.length} images.`);
          } else if (successCount === 0) {
            alert('Failed to load any images. Please check that the files are valid image formats.');
          }
        }
      };

      reader.readAsDataURL(file);
    });
  };

  const handleRemoveCityAdditionalImage = (cityId, imageIndex) => {
    setCities(cities.map(city => {
      if (city.id === cityId) {
        const newImages = [...(city.images || [])];
        const newPreviews = [...(city.imagesPreviews || [])];

        // Revoke blob URL to prevent memory leak
        const previewToRemove = newPreviews[imageIndex];
        if (previewToRemove && typeof previewToRemove === 'string' && previewToRemove.startsWith('blob:')) {
          URL.revokeObjectURL(previewToRemove);
        }

        newImages.splice(imageIndex, 1);
        newPreviews.splice(imageIndex, 1);
        return { ...city, images: newImages, imagesPreviews: newPreviews };
      }
      return city;
    }));
  };

  // Image upload helper functions
  const uploadCityImage = async (routeId, cityId, file, isCover = false) => {
    if (!file || !(file instanceof File)) {
      return null; // Already a URL or invalid
    }

    const folder = isCover ? 'coverImages' : 'images';
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const path = `routes/${routeId}/cities/${cityId}/${folder}/${fileName}`;

    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    // Wait for upload to complete
    await new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        null, // progress callback (optional)
        (error) => reject(error),
        () => resolve()
      );
    });

    // Get and return download URL
    return await getDownloadURL(uploadTask.snapshot.ref);
  };

  const uploadAllCityImages = async (routeId, citiesToUpload) => {
    const citiesWithUrls = await Promise.all(
      citiesToUpload.map(async (city) => {
        try {
          // Upload cover image if it's a File
          let coverImageUrl = city.coverImage;
          if (city.coverImage instanceof File) {
            console.log(`Uploading cover image for ${city.name}...`);
            coverImageUrl = await uploadCityImage(routeId, city.id, city.coverImage, true);
          }

          // Upload additional images
          const imageUrls = [];
          for (const img of city.images || []) {
            if (img instanceof File) {
              console.log(`Uploading additional image for ${city.name}...`);
              const url = await uploadCityImage(routeId, city.id, img, false);
              imageUrls.push(url);
            } else if (typeof img === 'string') {
              // Already a URL
              imageUrls.push(img);
            }
          }

          // Return city with URLs instead of File objects
          return {
            ...city,
            coverImage: coverImageUrl,
            images: imageUrls,
            // Keep URLs as preview fields so images display immediately after upload
            coverImagePreview: coverImageUrl,
            imagesPreviews: imageUrls
          };
        } catch (error) {
          console.error(`Error uploading images for ${city.name}:`, error);
          throw new Error(`Failed to upload images for ${city.name}. Please try again.`);
        }
      })
    );

    return citiesWithUrls;
  };

  const toggleCityExpand = (cityId) => {
    setExpandedCityId(expandedCityId === cityId ? null : cityId);
  };

  // Store Bullet Points management functions
  const handleAddHighlight = () => {
    if (storeBulletPoints.length >= 5) {
      alert('Maximum of 5 highlights allowed');
      return;
    }

    const newHighlight = {
      id: `highlight-${Date.now()}`,
      mainText: '',
      subText: ''
    };
    setStoreBulletPoints([...storeBulletPoints, newHighlight]);
  };

  const handleUpdateHighlight = (highlightId, field, value) => {
    setStoreBulletPoints(storeBulletPoints.map(highlight =>
      highlight.id === highlightId ? { ...highlight, [field]: value } : highlight
    ));
  };

  const handleRemoveHighlight = (highlightId) => {
    if (window.confirm('Remove this highlight?')) {
      setStoreBulletPoints(storeBulletPoints.filter(highlight => highlight.id !== highlightId));
    }
  };

  // Soft validation for duration fields (on blur, not on change)
  const validateMinDuration = () => {
    const numValue = parseInt(minDuration);

    if (minDuration && numValue < 3) {
      alert('Note: Minimum duration should be at least 3 days');
    }
    if (maxDuration && numValue > parseInt(maxDuration)) {
      alert('Note: Minimum duration should not exceed maximum duration');
    }
  };

  const validateMaxDuration = () => {
    const numValue = parseInt(maxDuration);

    if (maxDuration && numValue > 30) {
      alert('Note: Maximum duration should not exceed 30 days');
    }
    if (minDuration && numValue < parseInt(minDuration)) {
      alert('Note: Maximum duration should not be less than minimum duration');
    }
  };

  // Suggested hostels management functions
  const handleAddSuggestedHostel = () => {
    const newHostel = {
      id: `hostel-${Date.now()}`,
      name: '',
      cityId: cities[0]?.id || '',
      thoughts: ''
    };
    setSuggestedHostels([...suggestedHostels, newHostel]);
  };

  const handleUpdateSuggestedHostel = (hostelId, field, value) => {
    setSuggestedHostels(suggestedHostels.map(hostel =>
      hostel.id === hostelId ? { ...hostel, [field]: value } : hostel
    ));
  };

  const handleRemoveSuggestedHostel = (hostelId) => {
    if (window.confirm('Remove this hostel suggestion?')) {
      setSuggestedHostels(suggestedHostels.filter(hostel => hostel.id !== hostelId));
    }
  };

  // Save draft functionality
  const handleSaveDraft = async (isAutoSave = false) => {
    if (!auth.currentUser) {
      alert('You must be logged in to save');
      return;
    }

    // If route is live or off_store, save as revision instead
    if (['live', 'off_store'].includes(creatorStatus)) {
      if (!isAutoSave) {
        await handleSaveRevision();
      }
      return;
    }

    setSaving(true);

    try {
      // Generate docId first if this is a new submission
      let docId = submissionId;
      if (!docId) {
        docId = doc(collection(db, 'routes')).id;
        setSubmissionId(docId);
      }

      // Check if any cities have File objects that need uploading
      const hasFileObjects = cities.some(city =>
        city.coverImage instanceof File ||
        (city.images && city.images.some(img => img instanceof File))
      );

      let citiesToSave = cities;

      // Upload images if there are File objects
      if (hasFileObjects) {
        if (!isAutoSave) {
          console.log('Uploading images...');
        }
        citiesToSave = await uploadAllCityImages(docId, cities);
        // Update local state with URLs
        setCities(citiesToSave);
      }

      const routeData = {
        creatorId: auth.currentUser.uid,
        creatorStatus: creatorStatus,
        name: routeName, // Use 'name' to match store display
        selectedCountries,
        cities: citiesToSave,
        storeBulletPoints: storeBulletPoints.map(({ id, ...rest }) => rest),
        minDuration,
        maxDuration,
        bestTimeToVisit,
        budgetEstimate,
        difficultyLevel,
        colorScheme,
        storyBehindRoute,
        transportationBetweenCities,
        specialConsiderations,
        clothingRecommendations,
        suggestedHostels,
        updatedAt: serverTimestamp()
      };

      // Add createdAt if this is a new submission
      if (!submissionId) {
        routeData.createdAt = serverTimestamp();
      }

      await setDoc(doc(db, 'routes', docId), routeData, { merge: true });

      setLastSaved(new Date());

      if (!isAutoSave) {
        alert('Draft saved successfully!');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      if (!isAutoSave) {
        alert('Failed to save draft. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Keep ref updated with latest version of handleSaveDraft
  useEffect(() => {
    handleSaveDraftRef.current = handleSaveDraft;
  });

  // Save revision for live routes
  const handleSaveRevision = async () => {
    if (!auth.currentUser) {
      alert('You must be logged in to save');
      return;
    }

    if (!submissionId) {
      alert('Cannot create revision for unsaved route');
      return;
    }

    if (!window.confirm('This will create a revision request for admin approval. Your changes will not appear on the live route until approved. Continue?')) {
      return;
    }

    setSaving(true);

    try {
      // Get current live route data for comparison
      const currentRouteDoc = await getDoc(doc(db, 'routes', submissionId));
      const currentRouteData = currentRouteDoc.data();

      // Upload images first if needed
      const hasFileObjects = cities.some(city =>
        city.coverImage instanceof File ||
        (city.images && city.images.some(img => img instanceof File))
      );

      let citiesToSave = cities;
      if (hasFileObjects) {
        console.log('Uploading images for revision...');
        // Use a temporary ID for uploading revision images
        const tempId = `${submissionId}_revision_${Date.now()}`;
        citiesToSave = await uploadAllCityImages(tempId, cities);
        setCities(citiesToSave);
      }

      // Prepare revision data
      const revisionData = {
        name: routeName, // Use 'name' to match store display
        selectedCountries,
        cities: citiesToSave,
        storeBulletPoints: storeBulletPoints.map(({ id, ...rest }) => rest),
        minDuration,
        maxDuration,
        bestTimeToVisit,
        budgetEstimate,
        difficultyLevel,
        colorScheme,
        storyBehindRoute,
        transportationBetweenCities,
        specialConsiderations,
        clothingRecommendations,
        suggestedHostels
      };

      // Create revision document
      const revisionDocRef = doc(collection(db, 'routeRevisions'));
      await setDoc(revisionDocRef, {
        routeId: submissionId,
        creatorId: auth.currentUser.uid,
        status: 'pending',
        revisionData: revisionData,
        originalData: {
          name: currentRouteData.name || currentRouteData.routeName,
          selectedCountries: currentRouteData.selectedCountries,
          cities: currentRouteData.cities,
          storeBulletPoints: currentRouteData.storeBulletPoints,
          minDuration: currentRouteData.minDuration,
          maxDuration: currentRouteData.maxDuration,
          bestTimeToVisit: currentRouteData.bestTimeToVisit,
          budgetEstimate: currentRouteData.budgetEstimate,
          difficultyLevel: currentRouteData.difficultyLevel,
          colorScheme: currentRouteData.colorScheme,
          storyBehindRoute: currentRouteData.storyBehindRoute,
          transportationBetweenCities: currentRouteData.transportationBetweenCities,
          specialConsiderations: currentRouteData.specialConsiderations,
          clothingRecommendations: currentRouteData.clothingRecommendations,
          suggestedHostels: currentRouteData.suggestedHostels
        },
        submittedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      });

      alert('Revision submitted successfully! An admin will review your changes and approve them if appropriate.');
      navigate('/creator-dashboard');
    } catch (error) {
      console.error('Error saving revision:', error);
      alert('Failed to save revision. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Submit for review functionality
  const handleSubmitForReview = async () => {
    if (!auth.currentUser) {
      alert('You must be logged in to submit');
      return;
    }

    // Validation
    if (!routeName.trim()) {
      alert('Please enter a route name');
      return;
    }

    if (cities.length < 2) {
      alert('Please add at least 2 cities to your route');
      return;
    }

    // Check that all cities have required fields
    for (const city of cities) {
      if (!city.name || !city.countryCode || !city.cityVibes || !city.description || !city.latitude || !city.longitude) {
        alert(`Please complete all required fields for ${city.name || 'all cities'} (name, country, coordinates, city vibe, description)`);
        return;
      }

      // Validate coordinates are numbers
      const lat = parseFloat(city.latitude);
      const lng = parseFloat(city.longitude);
      if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        alert(`Invalid coordinates for ${city.name}. Latitude must be between -90 and 90, longitude between -180 and 180.`);
        return;
      }

      // Validate that cover image exists
      if (!city.coverImage) {
        alert(`Please upload a cover photo for ${city.name}`);
        return;
      }
    }

    if (storeBulletPoints.length < 3) {
      alert('Please add at least 3 route highlights');
      return;
    }

    if (!window.confirm('Are you sure you want to submit this route for review? You can still edit it after submission.')) {
      return;
    }

    setSaving(true);

    try {
      // Generate docId first if this is a new submission
      let docId = submissionId;
      if (!docId) {
        docId = doc(collection(db, 'routes')).id;
        setSubmissionId(docId);
      }

      // Always upload images before final submission
      console.log('Uploading images before submission...');
      const citiesToSave = await uploadAllCityImages(docId, cities);

      // Update local state with URLs
      setCities(citiesToSave);

      const routeData = {
        creatorId: auth.currentUser.uid,
        creatorStatus: 'submitted',
        name: routeName, // Use 'name' to match store display
        selectedCountries,
        cities: citiesToSave,
        storeBulletPoints: storeBulletPoints.map(({ id, ...rest }) => rest),
        minDuration,
        maxDuration,
        bestTimeToVisit,
        budgetEstimate,
        difficultyLevel,
        colorScheme,
        storyBehindRoute,
        transportationBetweenCities,
        specialConsiderations,
        clothingRecommendations,
        suggestedHostels,
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Add createdAt if this is a new submission
      if (!submissionId) {
        routeData.createdAt = serverTimestamp();
      }

      await setDoc(doc(db, 'routes', docId), routeData, { merge: true });

      setCreatorStatus('submitted');
      alert('Route submitted successfully! We will review it and get back to you soon.');
      navigate('/creator-dashboard');
    } catch (error) {
      console.error('Error submitting route:', error);
      alert('Failed to submit route. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="creator-submission-container">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          color: 'white'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px', animation: 'spin 1s linear infinite' }}>
            ⏳
          </div>
          <h2>Loading route data...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="creator-submission-container">
      <div className="creator-submission-content">
        <h1 className="creator-submission-title">Submit Your Route</h1>

        {/* Toggle Options */}
        <div className="toggle-options-container" style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '30px', alignItems: 'center' }}>
          {/* Auto-save Toggle */}
          <label className="autosave-toggle-label">
            <input
              type="checkbox"
              checked={autoSaveEnabled}
              onChange={(e) => setAutoSaveEnabled(e.target.checked)}
              className="autosave-checkbox"
            />
            <span className="autosave-text">
              Toggle 60-second autosave (to avoid losing progress)
            </span>
          </label>

          {/* Reveal All Fields Toggle */}
          <label className="autosave-toggle-label">
            <input
              type="checkbox"
              checked={showAllFields}
              onChange={(e) => setShowAllFields(e.target.checked)}
              className="autosave-checkbox"
            />
            <span className="autosave-text">
              Toggle Revealing All Fields (show all sections at once)
            </span>
          </label>
        </div>

        <div className="creator-form">
          {/* Route Name */}
          <div className="form-section">
            <label htmlFor="routeName" className="form-label">
              Route Name:
            </label>
            <input
              id="routeName"
              type="text"
              className="form-input"
              value={routeName}
              onChange={(e) => setRouteName(e.target.value)}
              placeholder="e.g., Albanian Alpine, Plan B: Budapest to Berlin"
            />
          </div>

          {/* Countries */}
          <div className="form-section">
            <label htmlFor="countrySearch" className="form-label">
              What country/countries does your route have cities in?
            </label>

            {/* Country Search Input */}
            <div className="country-search-wrapper">
              <input
                ref={searchInputRef}
                id="countrySearch"
                type="text"
                className="form-input"
                value={countrySearch}
                onChange={(e) => {
                  setCountrySearch(e.target.value);
                  setShowCountryDropdown(true);
                }}
                onFocus={() => setShowCountryDropdown(true)}
                placeholder="Search for a country..."
              />

              {/* Dropdown */}
              {showCountryDropdown && countrySearch && (
                <div ref={dropdownRef} className="country-dropdown">
                  {filteredCountries.length > 0 ? (
                    filteredCountries.slice(0, 8).map(country => (
                      <div
                        key={country.code}
                        className="country-dropdown-item"
                        onClick={() => handleSelectCountry(country)}
                      >
                        {country.name}
                      </div>
                    ))
                  ) : (
                    <div className="country-dropdown-empty">
                      No countries found
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected Countries */}
            {selectedCountries.length > 0 && (
              <div className="selected-countries">
                {selectedCountries.map(country => (
                  <div key={country.code} className="country-chip">
                    <span className="country-chip-name">{country.name}</span>
                    <button
                      type="button"
                      className="country-chip-remove"
                      onClick={() => handleRemoveCountry(country.code)}
                      aria-label={`Remove ${country.name}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cities Section */}
          {(selectedCountries.length > 0 || showAllFields) && (
            <div className="form-section">
              <label className="form-label">Cities on Your Route</label>
              <p className="form-helper-text">
                Add 2-6 cities on your route with detailed information (minimum 2, maximum 6)
              </p>

              {/* Cities List */}
              {cities.length > 0 && (
                <div className="cities-list">
                  {cities.map((city) => (
                    <div
                      key={city.id}
                      className={`city-card ${expandedCityId === city.id ? 'expanded' : ''}`}
                    >
                      {/* City Header */}
                      <div
                        className="city-card-header"
                        onClick={() => toggleCityExpand(city.id)}
                      >
                        <div className="city-card-title">
                          <span className="city-icon">
                            {city.cityVibes === 'Mountains' ? '⛰️' :
                             city.cityVibes === 'Beach' ? '🏖️' :
                             city.cityVibes === 'Urban' ? '🏙️' : '🏙️'}
                          </span>
                          <span className="city-name">
                            {city.name || 'New City'}
                            {city.countryCode && (
                              <span className="city-country">
                                {' '}in {COUNTRY_CODES.find(c => c.code === city.countryCode)?.name}
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="city-card-actions">
                          <button
                            type="button"
                            className="city-remove-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveCity(city.id);
                            }}
                          >
                            ×
                          </button>
                          <span className="city-expand-icon">
                            {expandedCityId === city.id ? '▲' : '▼'}
                          </span>
                        </div>
                      </div>

                      {/* City Form (Expanded) */}
                      {expandedCityId === city.id && (
                        <div className="city-card-content">
                          {/* City Name */}
                          <div className="city-field">
                            <label className="city-field-label">City Name *</label>
                            <input
                              type="text"
                              className="form-input"
                              value={city.name}
                              onChange={(e) => handleUpdateCity(city.id, 'name', e.target.value)}
                              placeholder="e.g., Tirana, Berat, Shkodër"
                            />
                          </div>

                          {/* Country Selection */}
                          <div className="city-field">
                            <label className="city-field-label">Country *</label>
                            <select
                              className="form-input form-select"
                              value={city.countryCode}
                              onChange={(e) => handleUpdateCity(city.id, 'countryCode', e.target.value)}
                            >
                              <option value="">Select country...</option>
                              {selectedCountries.map(country => (
                                <option key={country.code} value={country.code}>
                                  {country.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Cover Image Upload */}
                          <div className="city-field">
                            <label className="city-field-label">Cover Photo *</label>
                            <div className="image-upload-container">
                              {city.coverImagePreview ? (
                                <div className="image-preview">
                                  <img src={city.coverImagePreview} alt="Cover preview" />
                                  <button
                                    type="button"
                                    className="image-remove-btn"
                                    onClick={() => {
                                      // Revoke blob URL to prevent memory leak
                                      if (city.coverImagePreview && typeof city.coverImagePreview === 'string' && city.coverImagePreview.startsWith('blob:')) {
                                        URL.revokeObjectURL(city.coverImagePreview);
                                      }
                                      handleUpdateCity(city.id, 'coverImage', null);
                                      handleUpdateCity(city.id, 'coverImagePreview', null);
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : (
                                <label className="image-upload-label">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleCityImageUpload(city.id, e.target.files[0])}
                                    className="image-upload-input"
                                  />
                                  <div className="image-upload-placeholder">
                                    <span className="upload-icon">📷</span>
                                    <span>Click to upload cover photo</span>
                                  </div>
                                </label>
                              )}
                            </div>
                          </div>

                          {/* City Vibes */}
                          <div className="city-field">
                            <label className="city-field-label">City Vibes *</label>
                            <select
                              className="form-input form-select"
                              value={city.cityVibes}
                              onChange={(e) => handleUpdateCity(city.id, 'cityVibes', e.target.value)}
                            >
                              <option value="">Select vibe...</option>
                              <option value="Mountains">⛰️ Mountains</option>
                              <option value="Beach">🏖️ Beach</option>
                              <option value="Urban">🏙️ Urban</option>
                            </select>
                          </div>

                          {/* Coordinates */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                            <div className="city-field" style={{ marginBottom: 0 }}>
                              <label className="city-field-label">Latitude *</label>
                              <input
                                type="number"
                                step="any"
                                className="form-input"
                                value={city.latitude}
                                onChange={(e) => handleUpdateCity(city.id, 'latitude', e.target.value)}
                                placeholder="e.g., 41.3275"
                              />
                              <p className="field-helper-text" style={{ fontSize: '0.75rem', marginTop: '3px', color: 'rgba(255,255,255,0.6)' }}>
                                Between -90 and 90
                              </p>
                            </div>
                            <div className="city-field" style={{ marginBottom: 0 }}>
                              <label className="city-field-label">Longitude *</label>
                              <input
                                type="number"
                                step="any"
                                className="form-input"
                                value={city.longitude}
                                onChange={(e) => handleUpdateCity(city.id, 'longitude', e.target.value)}
                                placeholder="e.g., 19.8187"
                              />
                              <p className="field-helper-text" style={{ fontSize: '0.75rem', marginTop: '3px', color: 'rgba(255,255,255,0.6)' }}>
                                Between -180 and 180
                              </p>
                            </div>
                          </div>
                          <p className="field-helper-text" style={{ marginTop: '-10px', marginBottom: '15px' }}>
                            💡 Use <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer" style={{ color: '#FFD700', textDecoration: 'underline' }}>Google Maps</a> to find coordinates: right-click the city → click the coordinates to copy
                          </p>

                          {/* Tagline */}
                          <div className="city-field">
                            <label className="city-field-label">Tagline (Optional)</label>
                            <input
                              type="text"
                              className="form-input"
                              value={city.tagline}
                              onChange={(e) => handleUpdateCity(city.id, 'tagline', e.target.value)}
                              placeholder="e.g., The City of a Thousand Windows"
                              maxLength={100}
                            />
                          </div>

                          {/* Description */}
                          <div className="city-field">
                            <label className="city-field-label">Description *</label>
                            <textarea
                              className="form-textarea"
                              value={city.description}
                              onChange={(e) => handleUpdateCity(city.id, 'description', e.target.value)}
                              placeholder="Write a detailed description of this city (2-3 paragraphs)"
                              rows={5}
                            />
                          </div>

                          {/* Additional Images */}
                          <div className="city-field">
                            <label className="city-field-label">Additional Images (Optional)</label>
                            <p className="field-helper-text" style={{ marginBottom: '10px' }}>
                              Add up to 5 additional photos to showcase this city (these will appear in the city gallery)
                            </p>

                            {/* Display existing additional images */}
                            {city.imagesPreviews && city.imagesPreviews.length > 0 && (
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px', marginBottom: '10px' }}>
                                {city.imagesPreviews.map((preview, index) => (
                                  <div key={index} style={{ position: 'relative' }}>
                                    <img
                                      src={preview}
                                      alt={`City view ${index + 1}`}
                                      style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px' }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveCityAdditionalImage(city.id, index)}
                                      style={{
                                        position: 'absolute',
                                        top: '5px',
                                        right: '5px',
                                        background: 'rgba(0,0,0,0.7)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '24px',
                                        height: '24px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Upload button */}
                            {(!city.images || city.images.length < 5) && (
                              <label className="image-upload-label" style={{ display: 'block', cursor: 'pointer' }}>
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={(e) => handleCityAdditionalImagesUpload(city.id, e.target.files)}
                                  className="image-upload-input"
                                  style={{ display: 'none' }}
                                />
                                <div style={{
                                  border: '2px dashed rgba(255, 215, 0, 0.5)',
                                  borderRadius: '8px',
                                  padding: '20px',
                                  textAlign: 'center',
                                  background: 'rgba(255, 215, 0, 0.05)',
                                  cursor: 'pointer'
                                }}>
                                  <span className="upload-icon" style={{ fontSize: '32px', display: 'block', marginBottom: '8px' }}>📷</span>
                                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>
                                    Click to upload images ({city.images?.length || 0}/5)
                                  </span>
                                </div>
                              </label>
                            )}
                          </div>

                          {/* What To Do */}
                          <div className="city-field">
                            <label className="city-field-label">What To Do</label>
                            <textarea
                              className="form-textarea"
                              value={city.attractions}
                              onChange={(e) => handleUpdateCity(city.id, 'attractions', e.target.value)}
                              placeholder="List attractions, activities, and things to do in this city"
                              rows={4}
                            />
                          </div>

                          {/* What To Eat */}
                          <div className="city-field">
                            <label className="city-field-label">What To Eat</label>
                            <textarea
                              className="form-textarea"
                              value={city.food}
                              onChange={(e) => handleUpdateCity(city.id, 'food', e.target.value)}
                              placeholder="Recommend local foods, restaurants, and culinary experiences"
                              rows={4}
                            />
                          </div>

                          {/* Transportation */}
                          <div className="city-field">
                            <label className="city-field-label">Transportation</label>
                            <textarea
                              className="form-textarea"
                              value={city.transportation}
                              onChange={(e) => handleUpdateCity(city.id, 'transportation', e.target.value)}
                              placeholder="How to get around: buses, taxis, walking, etc."
                              rows={3}
                            />
                          </div>

                          {/* Services */}
                          <div className="city-field">
                            <label className="city-field-label">Services</label>
                            <textarea
                              className="form-textarea"
                              value={city.services}
                              onChange={(e) => handleUpdateCity(city.id, 'services', e.target.value)}
                              placeholder="Useful services: ATMs, hospitals, WiFi spots, etc."
                              rows={3}
                            />
                          </div>

                          {/* Testimonials */}
                          <div className="city-field">
                            <label className="city-field-label">Your Personal Experience</label>
                            <textarea
                              className="form-textarea"
                              value={city.testimonials}
                              onChange={(e) => handleUpdateCity(city.id, 'testimonials', e.target.value)}
                              placeholder="Share your personal story and experience in this city"
                              rows={4}
                            />
                          </div>

                          {/* Hostel Recommendations Section */}
                          <div className="hostel-recommendations-section">
                            <h3 className="hostel-section-title">Hostel Recommendations</h3>
                            <p className="hostel-section-subtitle">
                              Recommend one lit (party/social) hostel and one chill (quiet/relaxing) hostel
                            </p>

                            {/* Lit Hostel */}
                            <div className="city-field">
                              <label className="city-field-label">
                                🔥 Lit Hostel (Party/Social Vibe)
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                value={city.litHostel}
                                onChange={(e) => handleUpdateCity(city.id, 'litHostel', e.target.value)}
                                placeholder="e.g., Party Hostel Tirana, Social Hub Backpackers"
                              />
                            </div>

                            {/* Chill Hostel */}
                            <div className="city-field">
                              <label className="city-field-label">
                                😌 Chill Hostel (Quiet/Relaxing Vibe)
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                value={city.chillHostel}
                                onChange={(e) => handleUpdateCity(city.id, 'chillHostel', e.target.value)}
                                placeholder="e.g., Peaceful Garden Hostel, Quiet Corner Inn"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add City Button - Moved below cities list */}
              <button
                type="button"
                className="add-city-button"
                onClick={handleAddCity}
                disabled={cities.length >= 6}
                style={{
                  opacity: cities.length >= 6 ? 0.5 : 1,
                  cursor: cities.length >= 6 ? 'not-allowed' : 'pointer',
                  marginTop: cities.length > 0 ? '20px' : '0'
                }}
              >
                + Add City {cities.length > 0 && `(${cities.length}/6)`}
              </button>
            </div>
          )}

          {/* Route Highlights Section */}
          {(cities.length >= 2 || showAllFields) && (
            <div className="form-section">
              <label className="form-label">Route Highlights (Back of Card)</label>
              <p className="form-helper-text">
                Add 3-5 key highlights that will appear on the back of your route card when users flip it
              </p>

              {/* Highlights List */}
              {storeBulletPoints.length > 0 && (
                <div className="highlights-list">
                  {storeBulletPoints.map((highlight, index) => (
                    <div key={highlight.id} className="highlight-card">
                      <div className="highlight-card-header">
                        <span className="highlight-number">◆ Highlight {index + 1}</span>
                        <button
                          type="button"
                          className="city-remove-btn"
                          onClick={() => handleRemoveHighlight(highlight.id)}
                          aria-label="Remove highlight"
                        >
                          ×
                        </button>
                      </div>
                      <div className="highlight-card-content">
                        {/* Main Text */}
                        <div className="city-field">
                          <label className="city-field-label">Main Text *</label>
                          <input
                            type="text"
                            className="form-input"
                            value={highlight.mainText}
                            onChange={(e) => handleUpdateHighlight(highlight.id, 'mainText', e.target.value)}
                            placeholder="e.g., 10-14 Day Mountain Trek, Coastal Paradise"
                            maxLength={100}
                          />
                        </div>

                        {/* Sub Text */}
                        <div className="city-field">
                          <label className="city-field-label">Supporting Detail *</label>
                          <input
                            type="text"
                            className="form-input"
                            value={highlight.subText}
                            onChange={(e) => handleUpdateHighlight(highlight.id, 'subText', e.target.value)}
                            placeholder="e.g., Experience pristine wilderness and dramatic peaks"
                            maxLength={150}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Highlight Button - Moved below highlights list */}
              <button
                type="button"
                className="add-city-button"
                onClick={handleAddHighlight}
                disabled={storeBulletPoints.length >= 5}
                style={{
                  opacity: storeBulletPoints.length >= 5 ? 0.5 : 1,
                  cursor: storeBulletPoints.length >= 5 ? 'not-allowed' : 'pointer',
                  marginTop: storeBulletPoints.length > 0 ? '20px' : '0'
                }}
              >
                + Add Highlight {storeBulletPoints.length > 0 && `(${storeBulletPoints.length}/5)`}
              </button>
            </div>
          )}

          {/* Additional Route Information */}
          {(cities.length >= 2 || showAllFields) && (
            <div className="form-section">
              <label className="form-label">Additional Route Information</label>
              <p className="form-helper-text">
                Provide helpful details about planning and experiencing this route
              </p>

              {/* Story Behind the Route */}
              <div className="city-field" style={{ marginBottom: '30px' }}>
                <label className="city-field-label">Story Behind the Route</label>
                <textarea
                  className="form-textarea"
                  value={storyBehindRoute}
                  onChange={(e) => setStoryBehindRoute(e.target.value)}
                  placeholder="Share your personal story: What inspired you to create this route? What made this journey special? Why should others experience it? (2-3 paragraphs)"
                  rows={6}
                />
                <p className="field-helper-text">
                  Tell travelers why this route matters and what makes it unique from your perspective
                </p>
              </div>

              <div className="additional-info-grid">
                {/* Minimum Duration */}
                <div className="city-field">
                  <label className="city-field-label">Minimum Duration (Days)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={minDuration}
                    onChange={(e) => setMinDuration(e.target.value)}
                    onBlur={validateMinDuration}
                    placeholder="e.g., 7"
                    min="3"
                  />
                  <p className="field-helper-text">
                    Minimum 3 days recommended
                  </p>
                </div>

                {/* Maximum Duration */}
                <div className="city-field">
                  <label className="city-field-label">Maximum Duration (Days)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={maxDuration}
                    onChange={(e) => setMaxDuration(e.target.value)}
                    onBlur={validateMaxDuration}
                    placeholder="e.g., 14"
                    max="30"
                  />
                  <p className="field-helper-text">
                    Maximum 30 days recommended
                  </p>
                </div>

                {/* Best Time to Visit */}
                <div className="city-field">
                  <label className="city-field-label">Best Time to Visit</label>
                  <input
                    type="text"
                    className="form-input"
                    value={bestTimeToVisit}
                    onChange={(e) => setBestTimeToVisit(e.target.value)}
                    placeholder="e.g., May-September, Spring & Fall"
                  />
                </div>

                {/* Budget Estimate */}
                <div className="city-field">
                  <label className="city-field-label">Daily Budget Estimate (USD)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={budgetEstimate}
                    onChange={(e) => setBudgetEstimate(e.target.value)}
                    placeholder="e.g., 40"
                    min="0"
                    step="1"
                  />
                  <p className="field-helper-text">
                    Estimated daily spending on food, activities, and transportation (excluding accommodation)
                  </p>
                </div>

                {/* Difficulty Level */}
                <div className="city-field">
                  <label className="city-field-label">Difficulty Level</label>
                  <select
                    className="form-input form-select"
                    value={difficultyLevel}
                    onChange={(e) => setDifficultyLevel(e.target.value)}
                  >
                    <option value="">Select difficulty...</option>
                    <option value="Easy">Easy - Minimal planning required</option>
                    <option value="Moderate">Moderate - Some planning needed</option>
                    <option value="Challenging">Challenging - Requires careful planning</option>
                  </select>
                </div>
              </div>

              {/* Color Scheme Recommendations */}
              <div className="city-field" style={{ marginTop: '20px' }}>
                <label className="city-field-label">Color Scheme Recommendations</label>
                <textarea
                  className="form-textarea"
                  value={colorScheme}
                  onChange={(e) => setColorScheme(e.target.value)}
                  placeholder="Suggest colors that capture the vibe of your route (e.g., 'Deep blues and whites for coastal vibes', 'Earth tones and greens for mountain landscapes', 'Vibrant oranges and purples for sunset beach scenes')"
                  rows={3}
                />
                <p className="field-helper-text">
                  Help us design a route card that visually represents your journey
                </p>
              </div>

              {/* Transportation Between Cities */}
              <div className="city-field" style={{ marginTop: '20px' }}>
                <label className="city-field-label">Transportation Between Cities</label>
                <textarea
                  className="form-textarea"
                  value={transportationBetweenCities}
                  onChange={(e) => setTransportationBetweenCities(e.target.value)}
                  placeholder="Describe how to get from one city to the next (buses, trains, flights, etc.)"
                  rows={4}
                />
              </div>

              {/* Special Considerations */}
              <div className="city-field">
                <label className="city-field-label">Special Considerations</label>
                <textarea
                  className="form-textarea"
                  value={specialConsiderations}
                  onChange={(e) => setSpecialConsiderations(e.target.value)}
                  placeholder="Visa requirements, safety notes, cultural tips, language barriers, etc."
                  rows={4}
                />
              </div>

              {/* Clothing Recommendations */}
              <div className="city-field">
                <label className="city-field-label">What to Pack / Clothing Recommendations</label>
                <textarea
                  className="form-textarea"
                  value={clothingRecommendations}
                  onChange={(e) => setClothingRecommendations(e.target.value)}
                  placeholder="Layers for mountain weather, light clothing for summer, rain jacket, hiking boots, etc."
                  rows={4}
                />
              </div>
            </div>
          )}

          {/* Additional Hostel Suggestions */}
          {(cities.length >= 2 || showAllFields) && (
            <div className="form-section">
              <label className="form-label">Additional Hostel Suggestions</label>
              <p className="form-helper-text">
                Suggest other hostels you discovered (just provide the name and your thoughts - we'll handle adding them to the database)
              </p>

              {/* Suggested Hostels List */}
              {suggestedHostels.length > 0 && (
                <div className="highlights-list">
                  {suggestedHostels.map((hostel, index) => (
                    <div key={hostel.id} className="highlight-card">
                      <div className="highlight-card-header">
                        <span className="highlight-number">Hostel {index + 1}</span>
                        <button
                          type="button"
                          className="city-remove-btn"
                          onClick={() => handleRemoveSuggestedHostel(hostel.id)}
                          aria-label="Remove hostel suggestion"
                        >
                          ×
                        </button>
                      </div>
                      <div className="highlight-card-content">
                        {/* Hostel Name */}
                        <div className="city-field">
                          <label className="city-field-label">Hostel Name *</label>
                          <input
                            type="text"
                            className="form-input"
                            value={hostel.name}
                            onChange={(e) => handleUpdateSuggestedHostel(hostel.id, 'name', e.target.value)}
                            placeholder="e.g., Backpackers Paradise Tirana"
                          />
                        </div>

                        {/* City Selection */}
                        <div className="city-field">
                          <label className="city-field-label">City *</label>
                          <select
                            className="form-input form-select"
                            value={hostel.cityId}
                            onChange={(e) => handleUpdateSuggestedHostel(hostel.id, 'cityId', e.target.value)}
                          >
                            <option value="">Select city...</option>
                            {cities.map(city => (
                              <option key={city.id} value={city.id}>
                                {city.name || 'Unnamed City'}
                                {city.countryCode && ` (${COUNTRY_CODES.find(c => c.code === city.countryCode)?.name})`}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Your Thoughts */}
                        <div className="city-field">
                          <label className="city-field-label">Your Thoughts</label>
                          <textarea
                            className="form-textarea"
                            value={hostel.thoughts || ''}
                            onChange={(e) => handleUpdateSuggestedHostel(hostel.id, 'thoughts', e.target.value)}
                            placeholder="What did you think of this hostel? Why do you recommend it (or don't)?"
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Hostel Button */}
              <button
                type="button"
                className="add-city-button"
                onClick={handleAddSuggestedHostel}
                style={{
                  marginTop: suggestedHostels.length > 0 ? '20px' : '0'
                }}
              >
                + Add Hostel Suggestion
              </button>
            </div>
          )}

          {/* Save and Submit Section */}
          <div className="form-section" style={{ marginTop: '40px', borderTop: '2px solid rgba(255, 215, 0, 0.2)', paddingTop: '40px' }}>
            {/* Status Bar */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '25px',
              flexWrap: 'wrap',
              gap: '15px'
            }}>
              <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                {lastSaved && (
                  <span>
                    Last saved: {new Date(lastSaved).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </span>
                )}
                {!lastSaved && submissionId && <span>Loading...</span>}
                {!lastSaved && !submissionId && <span>Not saved yet</span>}
              </div>
              <div style={{
                padding: '6px 14px',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: '600',
                backgroundColor: creatorStatus === 'draft' ? '#666' :
                                 creatorStatus === 'submitted' ? '#3b82f6' :
                                 creatorStatus === 'needs_attention' ? '#f59e0b' :
                                 creatorStatus === 'live' ? '#10b981' :
                                 creatorStatus === 'off_store' ? '#ef4444' : '#8b5cf6',
                color: 'white'
              }}>
                {creatorStatus === 'draft' && 'Draft'}
                {creatorStatus === 'submitted' && 'Submitted'}
                {creatorStatus === 'needs_attention' && 'Needs Attention'}
                {creatorStatus === 'live' && 'Live'}
                {creatorStatus === 'off_store' && 'Off Store'}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '15px',
              flexWrap: 'wrap'
            }}>
              <button
                type="button"
                onClick={() => navigate('/creator-dashboard')}
                style={{
                  flex: '1',
                  minWidth: '200px',
                  padding: '14px 28px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.15)'}
                onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                disabled={saving}
              >
                ← Back to Dashboard
              </button>

              <button
                type="button"
                onClick={() => handleSaveDraft(false)}
                disabled={saving}
                style={{
                  flex: '1',
                  minWidth: '200px',
                  padding: '14px 28px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: saving ? 'wait' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: saving ? 0.5 : 1
                }}
              >
                {saving ? 'Saving...' : (['live', 'off_store'].includes(creatorStatus) ? 'Submit Revision' : 'Save Draft')}
              </button>

              {!['live', 'off_store'].includes(creatorStatus) && (
                <button
                  type="button"
                  onClick={handleSubmitForReview}
                  disabled={saving}
                  style={{
                    flex: '1',
                    minWidth: '200px',
                    padding: '14px 28px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                    color: '#072231',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: saving ? 'wait' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: saving ? 0.5 : 1,
                    boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)'
                  }}
                >
                  {creatorStatus === 'submitted' ? 'Update Submission' : 'Submit for Review'}
                </button>
              )}
            </div>

            {/* Helper Text */}
            <div style={{
              marginTop: '20px',
              padding: '15px',
              background: ['live', 'off_store'].includes(creatorStatus) ? 'rgba(255, 215, 0, 0.1)' : 'rgba(59, 130, 246, 0.1)',
              border: ['live', 'off_store'].includes(creatorStatus) ? '1px solid rgba(255, 215, 0, 0.3)' : '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '8px',
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.9rem',
              lineHeight: '1.6'
            }}>
              <strong>💡 Tip:</strong>
              {creatorStatus === 'draft' && ' Your progress is automatically saved every 60 seconds. You can come back anytime to continue editing your route.'}
              {creatorStatus === 'submitted' && ' Your route has been submitted and is awaiting review. You can still make edits while it\'s being reviewed.'}
              {creatorStatus === 'needs_attention' && ' Admin has requested changes. Please review the admin notes and resubmit.'}
              {['live', 'off_store'].includes(creatorStatus) && ' This route is live in the store. You can submit revision requests which will be reviewed by an admin before going live. Changes will not appear until approved.'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
